/**
 * BrowseGames — clean player browse experience.
 *
 * Flow:  Aggregator chips (currency-aware) + Category (driven by the shell
 *        Sidebar via redux) -> Provider rail -> Games grid -> launch (iframe).
 *
 * Single category nav = the shell Sidebar (menuState.categorty). This component
 * owns the aggregator selection (menuState.selectedAggregator) so the Sidebar can
 * show categories for the chosen aggregator.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from 'react-select';
import GeneralService from '../../apis/GeneralService';
import ApiURL from '../../apis/ApiURL';
import { createMd5hashFun } from '../../utility/createHash';
import { selectAggregator, selectCategory } from '../../redux/slices/changeMenuStateSlice';
import OWLLOGO from '../../assets/images/BYKUSH_Logo.png';
import './BrowseGames.css';

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || '683059b54a3eeeaad7ee3f18';
const GAME_PAGE_LIMIT = 24;

/* react-select styles — mirrors :root teal/amber tokens */
const selectStyles = {
    control: (b, s) => ({ ...b, minHeight: 40, background: 'var(--bg-card)', borderColor: s.isFocused ? 'var(--teal)' : 'var(--border-color)', boxShadow: 'none', borderRadius: 8, fontSize: 13 }),
    singleValue: b => ({ ...b, color: 'var(--text-primary)' }),
    placeholder: b => ({ ...b, color: 'var(--text-muted)' }),
    input: b => ({ ...b, color: 'var(--text-primary)' }),
    menu: b => ({ ...b, background: 'var(--bg-secondary)', borderRadius: 8, padding: 4, zIndex: 10 }),
    option: (b, s) => ({ ...b, background: s.isFocused ? 'rgba(46,201,176,.15)' : 'transparent', color: s.isFocused ? 'var(--teal)' : 'var(--text-secondary)', cursor: 'pointer', borderRadius: 6, fontSize: 13 }),
    indicatorSeparator: () => ({ display: 'none' }),
};



const BrowseGames = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const userDetails = useSelector(s => s.userDetailState?.user_details);
    const agg = useSelector(s => s.menuState?.selectedAggregator) || 'all';
    const cat = useSelector(s => s.menuState?.categorty) || 'all';

    const accountId = userDetails?.account_id;
    const accountKey = userDetails?.account_key;
    const playerId = userDetails?.player_id;
    const ready = !!(accountId && accountKey);

    const [aggregators, setAggregators] = useState([]);
    const [providers, setProviders] = useState([]);
    const [provider, setProvider] = useState(null); // null => all providers
    const [providerSearch, setProviderSearch] = useState('');

    const [games, setGames] = useState([]);
    const [gameTotal, setGameTotal] = useState(0);
    const [gamePage, setGamePage] = useState(1);
    const [gameTotalPages, setGameTotalPages] = useState(1);
    const [gameSearch, setGameSearch] = useState('');

    const [loading, setLoading] = useState({ agg: false, prov: false, games: false });
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const loadMoreRef = useRef(null);
    const inFlightRef = useRef(false);
    const [launchUrl, setLaunchUrl] = useState('');
    const [launchName, setLaunchName] = useState('');
    const [launching, setLaunching] = useState(false);
    const [autoLaunching, setAutoLaunching] = useState(false);
    const iframeWrapRef = useRef(null);
    const railRef = useRef(null);
    const didMount = useRef(false);



    const scrollRail = (dir) => { const el = railRef.current; if (el) el.scrollBy({ left: dir * 440, behavior: 'smooth' }); };

    /* hashed body: backend hashes {client_id, account_id} (+ provider_id when sent) */
    const buildBody = useCallback((extra = {}) => {
        const hashObj = { account_id: accountId, client_id: CLIENT_ID };
        if (extra.provider_id) hashObj.provider_id = extra.provider_id;
        const hash_token = createMd5hashFun(hashObj, accountKey);
        return { client_id: CLIENT_ID, account_id: accountId, hash_token, ...extra };
    }, [accountId, accountKey]);

    const loadAggregators = useCallback(async () => {
        setLoading(l => ({ ...l, agg: true }));
        try {
            const res = await GeneralService.apiPostCallRequest(ApiURL.getAggregators, buildBody());
            if (res?.err === false) setAggregators(res.data?.aggregators || []);
        } catch (e) { /* noop */ } finally { setLoading(l => ({ ...l, agg: false })); }
    }, [buildBody]);

    const loadProviders = useCallback(async (aggId, catId, search) => {
        setLoading(l => ({ ...l, prov: true }));
        try {
            const extra = { page_limit: 0 };
            if (aggId && aggId !== 'all') extra.provider_parent_id = aggId;
            if (catId && catId !== 'all') extra.category_id = catId;
            if (search) extra.search = search;
            const res = await GeneralService.apiPostCallRequest(ApiURL.getFilterProviders, buildBody(extra));
            setProviders(res?.err === false ? (res.data?.providers || []) : []);
        } catch (e) { setProviders([]); } finally { setLoading(l => ({ ...l, prov: false })); }
    }, [buildBody]);

    const loadGames = useCallback(async (aggId, catId, prov, page, search, append = false) => {
        if (append) setLoadingMore(true); else setLoading(l => ({ ...l, games: true }));
        try {
            const extra = { page, page_limit: GAME_PAGE_LIMIT };
            if (aggId && aggId !== 'all') extra.provider_parent_id = aggId;
            if (catId && catId !== 'all') extra.category_id = catId;
            if (prov?.value) extra.provider_id = prov.value;
            if (search) extra.search = search;
            const res = await GeneralService.apiPostCallRequest(ApiURL.getFilterGames, buildBody(extra));
            if (res?.err === false) {
                const newGames = res.data?.games || [];
                setGames(prev => append ? [...prev, ...newGames] : newGames);
                setGameTotal(res.data?.total || 0);
                setGameTotalPages(res.data?.total_pages || 1);
                setGamePage(page);

            } else if (!append) { setGames([]); setGameTotal(0); setGameTotalPages(1); setGamePage(1); }
        } catch (e) { if (!append) { setGames([]); setGameTotal(0); setGameTotalPages(1); setGamePage(1); } }
        finally { if (append) setLoadingMore(false); else setLoading(l => ({ ...l, games: false })); }
    }, [buildBody]);

    /* aggregators once */
    useEffect(() => { if (ready) loadAggregators(); /* eslint-disable-next-line */ }, [ready]);

    /* aggregator or category change -> reset provider + reload providers & games */
    useEffect(() => {
        if (!ready) return;
        setProvider(null);
        loadProviders(agg, cat, providerSearch);
        loadGames(agg, cat, null, 1, gameSearch, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agg, cat, ready]);

    /* provider search (debounced) */
    useEffect(() => {
        if (!ready || !didMount.current) { didMount.current = true; return; }
        const t = setTimeout(() => loadProviders(agg, cat, providerSearch), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerSearch]);

    /* game search (debounced) */
    useEffect(() => {
        if (!ready) return;
        const t = setTimeout(() => loadGames(agg, cat, provider, 1, gameSearch, false), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameSearch]);

    /* infinite scroll: load next page when the sentinel scrolls into view */
    loadMoreRef.current = () => {
        if (inFlightRef.current || loadingMore || loading.games) return;
        if (gamePage >= gameTotalPages) return;
        inFlightRef.current = true;
        const p = loadGames(agg, cat, provider, gamePage + 1, gameSearch, true);
        if (p && p.finally) p.finally(() => { inFlightRef.current = false; });
        else inFlightRef.current = false;
    };
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            entries => { if (entries[0].isIntersecting && loadMoreRef.current) loadMoreRef.current(); },
            { rootMargin: '400px 0px' }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const onSelectAgg = (id) => {
        dispatch(selectAggregator(id));
        dispatch(selectCategory('all')); // reset category when aggregator changes
    };
    const onSelectProvider = (p) => {
        setProvider(p);
        loadGames(agg, cat, p, 1, gameSearch, false);
    };

    const launchGame = async (game) => {
        if (launching) return;
        setLaunchName(game.game_name || 'Game');
        setLaunching(true);
        try {
            const params = { gameId: game._id, game_base_url_type: 'prd', lang: 'en', device: 'desktop', mode: 'real', client_id: CLIENT_ID, playerId };
            const hash_token = createMd5hashFun(params, accountKey);
            const res = await GeneralService.apiPostCallRequest(ApiURL.getGameUrl, { ...params, hash_token });
            const url = res?.data?.url || res?.err?.data?.url;
            if (url) setLaunchUrl(url);
            else alert(res?.message || res?.err?.message || 'Failed to launch game');
        } catch (e) { alert('Failed to launch game'); } finally { setLaunching(false); }
    };
    const closeGame = () => setLaunchUrl('');
    const goFullscreen = () => { const el = iframeWrapRef.current; if (el?.requestFullscreen) el.requestFullscreen(); };

    // Each game card is a real <a href="/?play=<id>">. Plain left-click keeps the in-app
    // player; ctrl/cmd/middle/right-click let the browser open the link in a new tab
    // (so "Open link in new tab" shows in the context menu). The new tab reads ?play=<id>
    // and launches the game full-tab via the effect below.
    const handleCardClick = (e, game) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return; // let the browser open the link
        e.preventDefault();
        launchGame(game);
    };

    // Auto-launch (full-tab) when opened as /?play=<gameId> in a new tab.
    useEffect(() => {
        if (!ready) return;
        const pid = new URLSearchParams(window.location.search).get('play');
        if (!pid) return;
        setAutoLaunching(true);
        (async () => {
            try {
                const params = { gameId: pid, game_base_url_type: 'prd', lang: 'en', device: 'desktop', mode: 'real', client_id: CLIENT_ID, playerId };
                const hash_token = createMd5hashFun(params, accountKey);
                // full-tab launch -> mask the upstream URL behind a ByKush wrapper (not hashed)
                const res = await GeneralService.apiPostCallRequest(ApiURL.getGameUrl, { ...params, wrapped: true, hash_token });
                const url = res?.data?.url || res?.err?.data?.url;
                if (url) { window.location.replace(url); return; }
                setAutoLaunching(false);
                alert(res?.message || res?.err?.message || 'Failed to launch game');
            } catch (e) { setAutoLaunching(false); alert('Failed to launch game'); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, playerId, accountKey]);

    const aggOptions = aggregators.map(a => ({ value: a.aggregator_id, label: `${a.name} (${a.gameCount})` }));
    const providerOptions = providers.map(p => ({ value: p.provider_id, label: `${p.provider_name}${p.provider_parent_name && p.provider_parent_name !== '-' ? '  ·  ' + p.provider_parent_name : ''}` }));
    const gameOptions = games.map(g => ({ value: g._id, label: g.game_name }));

    if (!ready) return (
        <div className="bg-root bg-logged-out">
            <div className="bg-empty-premium">
                <img className="bg-empty-owl" src={OWLLOGO} alt="ByKush" />
                <h1 className="bg-empty-title">Ready to Play?</h1>
                <p className="bg-empty-desc">Sign in to your account to unlock the full ByKush Casino experience.</p>
                <div className="bg-empty-cta">
                    Click <b>Sign In</b> at the top right <i className="fa-solid fa-arrow-up-right"></i>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-root">
            {/* ===== 3 dependent search dropdowns ===== */}
            <div className="bg-searchbar">
                <Select className="bg-sel" classNamePrefix="bgsel" styles={selectStyles} placeholder={intl.formatMessage({ id: 'AGGREGATOR' })}
                    value={aggOptions.find(o => o.value === agg) || null}
                    options={aggOptions} onChange={o => onSelectAgg(o ? o.value : 'all')} />
                <Select className="bg-sel" classNamePrefix="bgsel" styles={selectStyles} placeholder={intl.formatMessage({ id: 'PROVIDER' })} isClearable isSearchable
                    value={provider} options={providerOptions} onChange={o => onSelectProvider(o || null)} />
                <Select className="bg-sel" classNamePrefix="bgsel" styles={selectStyles} placeholder={intl.formatMessage({ id: 'SEARCHYOURGAME' })} isClearable isSearchable
                    value={null} options={gameOptions}
                    onInputChange={(v, meta) => { if (meta.action === 'input-change') setGameSearch(v); }}
                    onChange={o => { const g = games.find(x => x._id === o?.value); if (g) launchGame(g); }} />
            </div>

            {/* ===== Aggregator chips ===== */}
            <div className="bg-agg-row">
                {loading.agg && <span className="bg-mini-loader" />}
                {aggregators.map(a => (
                    <button key={a.aggregator_id} className={`bg-chip ${agg === a.aggregator_id ? 'active' : ''}`}
                        onClick={() => onSelectAgg(a.aggregator_id)}>
                        {a.name} <span className="bg-chip-count">{a.gameCount}</span>
                    </button>
                ))}
            </div>

            {/* ===== Provider rail ===== */}
            <div className="bg-prov-head">
                <span className="bg-head-title">
                    <span className="bg-head-ico"><i className="fa-solid fa-layer-group" /></span>
                    <FormattedMessage id="PROVIDER" /> {provider ? `· ${provider.label.split('  ·  ')[0]}` : ''}
                </span>
                <input className="bg-prov-search" placeholder={intl.formatMessage({ id: 'SEARCHPROVIDER' })} value={providerSearch}
                    onChange={e => setProviderSearch(e.target.value)} />
            </div>
            <div className="bg-rail-wrap">
                <button className="bg-rail-arrow left" onClick={() => scrollRail(-1)} aria-label="scroll left">‹</button>
                <div className="bg-prov-rail" ref={railRef}>
                    <button className={`bg-prov bg-prov-pinned ${!provider ? 'active' : ''}`} onClick={() => onSelectProvider(null)}>
                        <span className="bg-all-top"><FormattedMessage id="ALL" /></span>
                        <span className="bg-all-sub"><FormattedMessage id="PROVIDER" /></span>
                    </button>
                    {loading.prov
                        ? Array.from({ length: 10 }).map((_, i) => (
                            <div className="bg-prov bg-skel" key={'ps' + i}><div className="bg-skel-logo" /><div className="bg-skel-line" /></div>
                        ))
                        : providers.map(p => (
                            <button key={p.provider_id} className={`bg-prov ${provider?.value === p.provider_id ? 'active' : ''}`}
                                onClick={() => onSelectProvider({ value: p.provider_id, label: p.provider_name })} title={p.provider_parent_name}>
                                <img src={p.logo} alt="" onError={e => { e.target.style.visibility = 'hidden'; }} />
                                <span>{p.provider_name}</span>
                                {p.provider_parent_name && p.provider_parent_name !== '-' && <em className="bg-prov-agg">{p.provider_parent_name}</em>}
                            </button>
                        ))
                    }
                </div>
                <button className="bg-rail-arrow right" onClick={() => scrollRail(1)} aria-label="scroll right">›</button>
            </div>

            {/* ===== Games ===== */}
            <div className="bg-games-head">
                <span className="bg-head-title">
                    <span className="bg-head-ico"><i className="fa-solid fa-dice" /></span>
                    <FormattedMessage id="GAME" /> <b>{gameTotal.toLocaleString()}</b>
                </span>
            </div>
            {loading.games ? (
                <div className="bg-grid">
                    {Array.from({ length: GAME_PAGE_LIMIT }).map((_, i) => (
                        <div className="bg-card bg-skel" key={'gs' + i}><div className="bg-skel-img" /><div className="bg-skel-line" /><div className="bg-skel-line short" /></div>
                    ))}
                </div>
            ) : games.length === 0 ? (
                <div className="bg-empty">
                    <img className="bg-empty-owl" src={OWLLOGO} alt="ByKush" />
                    <FormattedMessage id="NOGAMEFOUND" /><br /><small>Try another category or aggregator.</small>
                </div>
            ) : (
                <div className="bg-grid">
                    {games.map((g, idx) => {
                        return (
                            <a className="bg-card" key={g._id} href={`/?play=${g._id}`} style={{ '--i': Math.min(idx, 12), textDecoration: 'none', color: 'inherit' }} onClick={(e) => handleCardClick(e, g)}>
                                <div className="bg-card-img">
                                    <img src={g.game_image || OWLLOGO} alt={g.game_name} loading="lazy" style={g.game_image ? undefined : { objectFit: 'contain', padding: '22%' }} onError={e => { e.target.onerror = null; e.target.src = OWLLOGO; e.target.style.objectFit = 'contain'; e.target.style.padding = '22%'; }} />
                                    {g.free_round_support && <span className="bg-badge fr">FREE ROUNDS</span>}
                                    <div className="bg-card-overlay">
                                        <span className="bg-card-playbtn">▶ Play</span>
                                    </div>
                                </div>
                                <div className="bg-card-name">{g.game_name}</div>
                                <div className="bg-card-prov">{g.provider_name}</div>
                            </a>
                        );
                    })}
                </div>
            )}

            {/* infinite-scroll sentinel (always mounted so the observer can attach) */}
            <div ref={sentinelRef} className="bg-sentinel" aria-hidden="true" />
            {loadingMore && <div className="bg-loader"><span className="bg-spinner" /> Loading more…</div>}
            {!loading.games && games.length > 0 && gamePage >= gameTotalPages && gameTotalPages > 1 && (
                <div className="bg-end">You've reached the end · {gameTotal.toLocaleString()} games</div>
            )}

            {/* ===== Launch overlay (iframe, current window) ===== */}
            {(launchUrl || launching) && (
                <div className="bg-launch">
                    <div className="bg-launch-bar">
                        <span>{launchName}</span>
                        <div>
                            <button onClick={goFullscreen}>⛶ Fullscreen</button>
                            <button className="bg-launch-close" onClick={closeGame}>✕ Close</button>
                        </div>
                    </div>
                    <div className="bg-launch-frame" ref={iframeWrapRef}>
                        {launching && (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-solid)', zIndex: 10, gap: '20px' }}>
                                <div className="brand-loader-ring" style={{ width: '80px', height: '80px' }}>
                                    <img src={OWLLOGO} alt="Launching..." style={{ width: '64px', borderRadius: '50%', position: 'relative', zIndex: 1 }} onError={e => { e.target.style.display = 'none'; }} />
                                </div>
                                <div style={{ color: 'var(--teal-bright)', fontSize: '14px', fontWeight: 600, letterSpacing: '1px' }}>LAUNCHING...</div>
                            </div>
                        )}
                        {launchUrl && <iframe title="game" src={launchUrl} allow="autoplay; fullscreen" allowFullScreen />}
                    </div>
                </div>
            )}

            {/* ===== Full-tab launch loader (when opened via /?play=<id> in a new tab) ===== */}
            {autoLaunching && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-solid, #0f1419)', gap: '20px' }}>
                    <div className="brand-loader-ring" style={{ width: '80px', height: '80px' }}>
                        <img src={OWLLOGO} alt="Launching..." style={{ width: '64px', borderRadius: '50%', position: 'relative', zIndex: 1 }} onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div style={{ color: 'var(--teal-bright, #2ec9b0)', fontSize: '14px', fontWeight: 600, letterSpacing: '1px' }}>LAUNCHING...</div>
                </div>
            )}
        </div>
    );
};

export default BrowseGames;
